<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import type { CustomerDetails } from '@/types/types'
import type { LoyaltyReward } from '@/types/global'
import type { RewardClaimed } from '@/composables/customerRewards'

interface ExtendedRewardClaimed extends RewardClaimed {
  count: number
}

const { stores } = useLocalStorage()
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { claimCustomerReward, requestCustomerMerchantUnusedClaimedRewards, useCustomerReward } = useCustomerRewards()

const props = defineProps<{
  isOpen: boolean
  rewards: LoyaltyReward[] | null
  customer: CustomerDetails & { id: string }
}>()

const emit = defineEmits<{
  (e: 'on-claim-reward'): void
  (e: 'close-customer-rewards-modal'): void
}>()

const toggleValue = ref<'claimedRewards' | 'availableRewards'>('claimedRewards')
const claimedRewards = ref<RewardClaimed[]>([])
const isClaimConfirmationModalOpen = ref(false)
const isUseConfirmationModalOpen = ref(false)
const rewardToClaim = ref<LoyaltyReward | RewardClaimed | null>(null)
const selectedStoreId = ref(stores.value[0].id)
const claimRewardProcessing = ref(false)
const claimRewardAndUseProcessing = ref(false)
const toggleStates = <{ value: 'claimedRewards' | 'availableRewards'; title: string }[]>[
  {
    value: 'claimedRewards',
    title: t('pages.customer.customerDetailPage.reward.modal.claimed')
  },
  {
    value: 'availableRewards',
    title: t('pages.customer.customerDetailPage.reward.modal.available')
  }
]

const modalUseConfirmationTexts = computed(() => {
  return {
    title: t('pages.customer.customerDetailPage.reward.modal.useConfirmation.title', {
      rewardName: rewardToClaim.value?.name
    }),
    linkText: null,
    modal: 'useConfirmation'
  }
})
const modalRewardTexts = computed(() => {
  return {
    title: t('pages.customer.customerDetailPage.reward.button'),
    modal: 'main',
    linkText: isClaimConfirmationModalOpen.value
      ? t('pages.customer.customerDetailPage.reward.modal.claimFor', { points: rewardToClaim.value?.points })
      : null
  }
})
const modalTexts = computed(() => {
  return isClaimConfirmationModalOpen.value ? modalUseConfirmationTexts.value : modalRewardTexts.value
})
const modalsStates = computed(() => ({
  isMainModalOpen: props.isOpen,
  isUseConfirmationModalOpen: isUseConfirmationModalOpen.value || !!route?.query?.useConfirmation,
  isClaimConfirmationModalOpen: isClaimConfirmationModalOpen.value || !!route?.query?.claimConfirmation
}))
const formattedStores = computed(() => {
  return stores.value.map((store) => {
    return {
      id: store.id,
      text: store.name
    }
  })
})

const fetchClaimedRewards = async () => {
  claimedRewards.value = await requestCustomerMerchantUnusedClaimedRewards(props.customer.id)
}
const handleClickClaimedReward = async (reward: ExtendedRewardClaimed) => {
  rewardToClaim.value = reward

  await useCustomerReward(reward.hash)
  await fetchClaimedRewards()
  rewardToClaim.value = null
}
const handleClickReward = async (reward: LoyaltyReward) => {
  rewardToClaim.value = reward
  isClaimConfirmationModalOpen.value = true
}

const claimReward = async () => {
  claimRewardProcessing.value = true

  const rewardId = rewardToClaim.value?.id
  const storeId = selectedStoreId.value

  if (!rewardId || !storeId) {
    throw new Error('Reward ID or store ID is missing')
  }

  try {
    await claimCustomerReward(props.customer.id, rewardId, storeId)
    await redirectAndFetchClaimedRewards()
  } finally {
    claimRewardProcessing.value = false
  }
}

const claimRewardAndUse = async () => {
  claimRewardAndUseProcessing.value = true

  const rewardId = rewardToClaim.value?.id
  const storeId = selectedStoreId.value

  if (!rewardId || !storeId) {
    throw new Error('Reward ID or store ID is missing')
  }

  try {
    const claimedReward = await claimCustomerReward(props.customer.id, rewardId, storeId)
    await useCustomerReward(claimedReward.hash)
    await redirectAndFetchClaimedRewards()
    claimRewardAndUseProcessing.value = false
  } finally {
    claimRewardAndUseProcessing.value = false
  }
}

const handleStoreSelection = (id: number) => {
  selectedStoreId.value = id
}

const closeModal = () => {
  if (modalTexts.value.modal === 'main') {
    router.push({ query: {} })
    emit('close-customer-rewards-modal')
  } else {
    isClaimConfirmationModalOpen.value = false
    rewardToClaim.value = null
  }
}

const getOverlayText = (reward: LoyaltyReward) => {
  if (reward.hasReachedClaimLimit) {
    return t('pages.customer.customerDetailPage.reward.modal.card.reachedClaimLimit')
  }

  return props.customer.points >= reward.points
    ? undefined
    : t('pages.customer.customerDetailPage.reward.modal.card.pointsDifference', {
        points: reward.points - props.customer.points
      })
}
const getButtonText = (reward: LoyaltyReward) => {
  const isRewardClaimable = props.customer.points >= reward.points
  const key = isRewardClaimable ? 'pointCost' : 'unclaimable'

  return t(`pages.customer.customerDetailPage.reward.modal.${key}`, { points: reward.points })
}
const redirectAndFetchClaimedRewards = async () => {
  await fetchClaimedRewards()
  closeModal()
  emit('on-claim-reward')
}

const groupedClaimedRewardsByRewardId = computed<ExtendedRewardClaimed[]>(() => {
  return Object.values(
    claimedRewards.value.reduce<Record<number, ExtendedRewardClaimed>>((acc, reward) => {
      const rewardId = reward.id

      if (acc[rewardId]) {
        acc[rewardId].count += 1
      } else {
        acc[rewardId] = { ...reward, count: 1 }
      }

      return acc
    }, {})
  )
})

onMounted(async () => {
  await fetchClaimedRewards()
})
</script>

<template>
  <BaseModal
    :is-open="modalsStates.isMainModalOpen"
    :heading-title="modalTexts.title"
    :link-text="modalTexts.linkText"
    :is-processing="claimRewardProcessing || claimRewardAndUseProcessing"
    @close="closeModal"
  >
    <div class="flex h-full w-full flex-1 flex-col items-start overflow-auto rounded-b-md">
      <AppSkeleton v-if="claimRewardProcessing || claimRewardAndUseProcessing" />

      <div
        v-else-if="modalsStates.isClaimConfirmationModalOpen && rewardToClaim"
        class="flex w-full flex-col gap-6 px-4 text-left"
      >
        <button type="button" class="flex items-center gap-2 text-sm text-muted-foreground" @click="closeModal">
          <BaseIcon name="HeroArrowLeftIconSolid" class="h-5 w-5" />
          {{ t('pages.customer.customerDetailPage.reward.back') }}
        </button>

        <div class="flex w-full gap-4">
          <div
            :style="{ backgroundImage: `url(${rewardToClaim?.pictureUrl})` }"
            class="flex h-[150px] w-[150px] items-center justify-center rounded-md bg-cover bg-center"
          />
          <div>
            <p class="font-bold">{{ rewardToClaim?.name }}</p>
            <p class="text-sm text-muted-foreground">
              {{ t('pages.customer.customerDetailPage.reward.modal.pointCost', { points: rewardToClaim?.points }) }}
            </p>
          </div>
        </div>

        <div class="flex w-full flex-col gap-2">
          <label class="block text-sm font-medium capitalize text-foreground" for="store">
            {{ t('global.words.store') }}
          </label>
          <ComboboxSelect
            value-key="id"
            label-key="text"
            name="store"
            :placeholder="t('pages.customer.customerPage.blocks.create.fields.chooseStore')"
            :model-value="selectedStoreId"
            :options="formattedStores"
            trigger-class="w-full"
            @update:model-value="handleStoreSelection"
          />
        </div>

        <div class="flex w-full items-center justify-center gap-4">
          <Button variant="outline" :disabled="claimRewardProcessing" class="min-w-0 flex-1" @click="claimReward">
            <Loader2 v-if="claimRewardProcessing" class="animate-spin" />
            {{ t('pages.customer.customerDetailPage.reward.modal.useConfirmation.later') }}
          </Button>
          <Button
            variant="default-primary"
            :disabled="claimRewardAndUseProcessing"
            class="min-w-0 flex-1"
            @click="claimRewardAndUse"
          >
            <Loader2 v-if="claimRewardAndUseProcessing" class="animate-spin" />
            {{ t('pages.customer.customerDetailPage.reward.modal.useConfirmation.linkText') }}
          </Button>
        </div>
      </div>

      <div v-else class="flex w-full flex-col gap-6 pb-6">
        <Tabs v-model="toggleValue" class="w-full">
          <TabsList class="w-full">
            <TabsTrigger v-for="state in toggleStates" :key="state.value" :value="state.value" class="flex-1">
              {{ state.title }}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div class="grid grid-cols-3 gap-6">
          <template v-if="toggleValue === 'claimedRewards'">
            <div v-for="cr in groupedClaimedRewardsByRewardId" :key="cr.id">
              <Reward
                :title="cr.name"
                :img="cr?.pictureUrl"
                :badge-text="`x${String(cr.count)}`"
                :button-text="t('pages.customer.customerDetailPage.reward.modal.card.use')"
                button-text-color="white"
                @click-button="handleClickClaimedReward(cr)"
              />
            </div>
          </template>
          <template v-else>
            <div v-for="reward in rewards" :key="reward.id">
              <Reward
                :title="reward?.name"
                :img="reward?.pictureUrl"
                :overlay-text="getOverlayText(reward)"
                :is-clickable="customer.points >= reward.points && !reward.hasReachedClaimLimit"
                :button-text="getButtonText(reward)"
                button-text-color="white"
                @click="handleClickReward(reward)"
                @click-button="handleClickReward(reward)"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
